// =============================================================
// DealScopePicker — the ONE "Deal > Deal Detail > Project" list a task or note
// is filed through.
//
// A project used to be a second box under the deal picker (`ProjectPicker`,
// retired). It is now a row in the same list: each deal's projects sit under the
// deal (or under the detail a project is scoped to), and one choice resolves the
// deal, the detail and the project together (`scopeFromPick`).
//
// Two shapes:
//   DealScopePicker         — the free choice (Tasks page, task editor, Harvi's
//                             task draft, Global Notes rows). Every deal, every
//                             detail, every project.
//   FixedScopeProjectPicker — the deal's own panels, where the deal (and maybe
//                             the detail) is already fixed by the page: the same
//                             list narrowed to that scope, so the only choice
//                             left is the project. Renders nothing when the scope
//                             has no projects, so a deal that never used them
//                             looks exactly as it did before.
//
// Project rows are a task/note concern only; the pickers that file emails,
// documents or chat keep calling `useDealPickerItems()` without projects.
// =============================================================

import { InlineSearchPicker, useDealPickerItems, dealPickerValueFor } from '../tasks/TaskComponents';

// Resolve a picker value into the scope it files the item under. A project row
// carries its own deal and detail; a deal row is deal-level with no project.
export function scopeFromPick(value, items) {
  if (!value) return { dealId: '', dealDetailId: '', projectId: '' };
  const hit = (items || []).find(i => i.value === value);
  return {
    dealId:       hit?.dealID || (value.startsWith('deal:') ? value.slice(5) : ''),
    dealDetailId: hit?.dealDetailID || (value.startsWith('detail:') ? value.slice(7) : ''),
    projectId:    hit?.projectID || (value.startsWith('project:') ? value.slice(8) : ''),
  };
}

// The rows a fixed scope offers: the scope itself (meaning "no project") plus
// the projects that belong to it — a detail's own projects and the deal-level
// ones, never a sibling detail's (the old ProjectPicker's includeDealLevel rule).
export function fixedScopeItems(items, { dealId, dealDetailId } = {}) {
  const norm = v => String(v || '').toLowerCase();
  const scopeRow = (items || []).find(i => !i.projectID && (dealDetailId
    ? norm(i.dealDetailID) === norm(dealDetailId)
    : norm(i.dealID) === norm(dealId) && !i.dealDetailID));
  const projects = (items || []).filter(i => i.projectID && norm(i.dealID) === norm(dealId)
    && (!dealDetailId || !i.dealDetailID || norm(i.dealDetailID) === norm(dealDetailId)))
    .map(i => ({ ...i, indent: true }));
  if (!projects.length) return [];
  return [...(scopeRow ? [{ ...scopeRow, indent: false }] : []), ...projects];
}

export const DealScopePicker = ({
  value, onChange, label = 'Deal / Deal Detail / Project (optional)',
  placeholder = 'Search deals, details or projects…', clearLabel = '— No deal —',
  includeProjects = true,
}) => {
  const { dealPickerItems } = useDealPickerItems({ includeProjects });
  return (
    <InlineSearchPicker label={label} value={value}
      onChange={(v) => onChange(scopeFromPick(v, dealPickerItems), v)}
      items={dealPickerItems} placeholder={placeholder} clearLabel={clearLabel} />
  );
};

export const FixedScopeProjectPicker = ({
  dealId, dealDetailId, value, onChange, disabled = false,
  label = 'Deal / Deal Detail / Project',
}) => {
  const { dealPickerItems } = useDealPickerItems({ includeProjects: true, onlyDealId: dealId });
  const items = fixedScopeItems(dealPickerItems, { dealId, dealDetailId });
  if (!items.length) return null;
  const scopeValue = items[0]?.projectID ? '' : items[0]?.value;
  const current = value ? dealPickerValueFor({ projectId: value }) : (scopeValue || '');
  return (
    <div style={disabled ? { opacity: 0.6, pointerEvents: 'none' } : undefined}>
      <InlineSearchPicker label={label} value={current}
        onChange={(v) => onChange(scopeFromPick(v, items).projectId || '')}
        items={items} placeholder="Search projects…" clearLabel="— No project —" />
    </div>
  );
};

export default DealScopePicker;
